import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Search, ChevronRight, User, Building, Calendar, DollarSign, X, Edit, FileText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import ConfirmModal from '@/components/ConfirmModal';
import AlertasVencimento from '@/components/AlertasVencimento';

const FinanceirosAgrupados = ({ tipo }) => {
  const [financeiros, setFinanceiros] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [resumoAlertas, setResumoAlertas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });
  const [saving, setSaving] = useState(false);
  const [pessoaSelecionada, setPessoaSelecionada] = useState(null);
  const [movimentacoesPessoa, setMovimentacoesPessoa] = useState([]);
  const [editingFinanceiro, setEditingFinanceiro] = useState(null);
  const [formData, setFormData] = useState({
    pessoa_id: '',
    tipo: tipo,
    categoria_id: '',
    titulo: '',
    descricao: '',
    documento: '',
    parcela_atual: 1,
    parcelas_total: 1,
    valor_original: '',
    desconto: 0,
    acrescimo: 0,
    emissao: new Date().toISOString().split('T')[0],
    vencimento: new Date().toISOString().split('T')[0],
    vencimento_final: '',
    forma_pagamento: 'PIX',
    banco: '',
    status: 'em_aberto'
  });

  // Calcular parcelas quando mudar vencimento inicial ou final
  useEffect(() => {
    if (formData.vencimento && formData.vencimento_final && formData.vencimento_final > formData.vencimento) {
      const inicio = new Date(formData.vencimento);
      const fim = new Date(formData.vencimento_final);
      
      const meses = (fim.getFullYear() - inicio.getFullYear()) * 12 + (fim.getMonth() - inicio.getMonth()) + 1;
      
      if (meses > 0 && meses <= 60) {
        setFormData(prev => ({ ...prev, parcelas_total: meses }));
      }
    }
  }, [formData.vencimento, formData.vencimento_final]);
  const [filtros, setFiltros] = useState({
    data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, [tipo]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('tipo', tipo);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);

      const [finResponse, pessoasResponse, catResponse, bancosResponse, resumoResponse] = await Promise.all([
        api.get(`/financeiros?${params.toString()}`),
        api.get('/pessoas'),
        api.get('/categorias'),
        api.get('/bancos'),
        api.get('/alertas/resumo')
      ]);
      
      setFinanceiros(finResponse.data);
      setPessoas(pessoasResponse.data);
      setCategorias(catResponse.data.filter(c => c.tipo === tipo || c.tipo === 'ambos'));
      setBancos(bancosResponse.data);
      setResumoAlertas(resumoResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = (pessoa) => {
    const movs = financeiros.filter(f => f.pessoa_id === pessoa.id);
    setPessoaSelecionada(pessoa);
    setMovimentacoesPessoa(movs);
    setModalOpen(true);
  };

  const handleEditar = (mov, e) => {
    e.stopPropagation();
    setEditingFinanceiro(mov);
    setFormData({
      pessoa_id: mov.pessoa_id,
      tipo: tipo,
      categoria_id: mov.categoria_id,
      titulo: mov.titulo,
      valor_original: mov.valor_original,
      desconto: mov.desconto || 0,
      acrescimo: mov.acrescimo || 0,
      banco: mov.banco || '',
      forma_pagamento: mov.forma_pagamento,
      emissao: mov.emissao,
      vencimento: mov.vencimento,
      status: mov.status
    });
    setModalEdicao(true);
  };

  const handleNovoLancamento = () => {
    setEditingFinanceiro(null);
    setFormData({
      pessoa_id: '',
      tipo: tipo,
      categoria_id: '',
      titulo: '',
      descricao: '',
      documento: '',
      parcela_atual: 1,
      parcelas_total: 1,
      valor_original: '',
      desconto: 0,
      acrescimo: 0,
      emissao: new Date().toISOString().split('T')[0],
      vencimento: new Date().toISOString().split('T')[0],
      vencimento_final: '',
      forma_pagamento: 'PIX',
      banco: '',
      status: 'em_aberto'
    });
    setModalEdicao(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevenir duplo clique
    
    setSaving(true);
    try {
      const dataToSubmit = {
        ...formData,
        valor_original: parseFloat(formData.valor_original),
        desconto: parseFloat(formData.desconto) || 0,
        acrescimo: parseFloat(formData.acrescimo) || 0
      };

      if (editingFinanceiro) {
        await api.put(`/financeiros/${editingFinanceiro.id}`, dataToSubmit);
        toast.success('Lançamento atualizado!');
      } else {
        // Se tem mais de 1 parcela, usar endpoint de parcelamento
        if (dataToSubmit.parcelas_total > 1) {
          await api.post('/financeiros/parcelado', dataToSubmit);
          toast.success(`${dataToSubmit.parcelas_total} parcelas criadas!`);
        } else {
          await api.post('/financeiros', dataToSubmit);
          toast.success('Lançamento criado!');
        }
      }
      
      setModalEdicao(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
      console.error('Erro detalhado:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRecibo = async (id, e) => {
    e.stopPropagation();
    const toastId = toast.loading('Gerando recibo...');
    try {
      const { data: blob } = await api.get(`/financeiros/${id}/recibo`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recibo_BoraCar_${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success('Recibo gerado!', { id: toastId });
    } catch (error) {
      const msg = error.response?.status === 404 ? 'Recibo não encontrado' : 'Erro ao gerar recibo';
      toast.error(msg, { id: toastId });
    }
  };

  const handleExcluir = async (id) => {
    try {
      await api.delete(`/financeiros/${id}/excluir`);
      toast.success('Lançamento excluído!');
      loadData();
      // Recarregar movimentações da pessoa se modal estiver aberto
      if (pessoaSelecionada) {
        const movs = financeiros.filter(f => f.pessoa_id === pessoaSelecionada.id && f.id !== id);
        setMovimentacoesPessoa(movs);
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
  };

  const pessoasComMovimentacoes = pessoas
    .map(pessoa => {
      const movs = financeiros.filter(f => f.pessoa_id === pessoa.id);
      const total = movs.reduce((sum, m) => sum + m.valor_liquido, 0);
      const totalPago = movs.filter(m => m.status === 'pago').reduce((sum, m) => sum + m.valor_liquido, 0);
      const totalAberto = movs.filter(m => m.status === 'em_aberto').reduce((sum, m) => sum + m.valor_liquido, 0);
      const totalVencido = movs.filter(m => m.status === 'vencido').reduce((sum, m) => sum + m.valor_liquido, 0);
      
      return {
        ...pessoa,
        movimentacoes: movs,
        total,
        totalPago,
        totalAberto,
        totalVencido,
        quantidade: movs.length
      };
    })
    .filter(p => p.quantidade > 0)
    .sort((a, b) => a.nome_razao.localeCompare(b.nome_razao));

  const totalGeral = pessoasComMovimentacoes.reduce((sum, p) => sum + p.total, 0);

  return (
    <div className="space-y-6 animate-slide-up">
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={() => handleExcluir(confirmDelete.id)}
        title="Excluir Lançamento"
        message="Deseja realmente excluir este lançamento? Esta ação não pode ser revertida."
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Contas a {tipo === 'pagar' ? 'Pagar' : 'Receber'} por Pessoa
          </h1>
          <p className="text-slate-600">Visualização agrupada por cliente, fornecedor ou motorista</p>
        </div>
        <Button 
          onClick={handleNovoLancamento}
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
        >
          + Novo Lançamento
        </Button>
      </div>

      {/* Alertas de Vencimento */}
      {resumoAlertas && (
        <AlertasVencimento resumo={resumoAlertas} tipo={tipo} />
      )}

      {/* Modal de Edição/Criação - z-index MAIOR para aparecer na frente */}
      {modalEdicao && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setModalEdicao(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editingFinanceiro ? 'Editar Lançamento' : 'Novo Lançamento'}</h2>
              <button onClick={() => setModalEdicao(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pessoa</Label>
                  <select
                    value={formData.pessoa_id}
                    onChange={(e) => setFormData({ ...formData, pessoa_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecione...</option>
                    {pessoas.filter(p => p.ativo).map(p => (
                      <option key={p.id} value={p.id}>{p.nome_razao}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <select
                    value={formData.categoria_id}
                    onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecione...</option>
                    {categorias.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Título</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Valor Original</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.valor_original}
                    onChange={(e) => setFormData({ ...formData, valor_original: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Desconto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.desconto}
                    onChange={(e) => setFormData({ ...formData, desconto: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Acréscimo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.acrescimo}
                    onChange={(e) => setFormData({ ...formData, acrescimo: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Parcelas (1 a 60)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.parcelas_total}
                    onChange={(e) => setFormData({ ...formData, parcelas_total: parseInt(e.target.value) || 1, vencimento_final: '' })}
                    disabled={formData.vencimento_final !== ''}
                  />
                  {formData.parcelas_total > 1 && (
                    <p className="text-xs text-slate-600 mt-1">
                      Valor por parcela: R$ {(parseFloat(formData.valor_original || 0) / formData.parcelas_total).toFixed(2).replace('.', ',')}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Banco</Label>
                  <select
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Selecione...</option>
                    {bancos.map(b => (
                      <option key={b.id} value={b.nome}>{b.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Forma de Pagamento</Label>
                  <select
                    value={formData.forma_pagamento}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="input-field"
                  >
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                    <option value="Cartão de Débito">Cartão de Débito</option>
                    <option value="PIX">PIX</option>
                    <option value="Boleto">Boleto</option>
                    <option value="Transferência">Transferência</option>
                  </select>
                </div>
                <div>
                  <Label>Documento/Nota</Label>
                  <Input
                    value={formData.documento}
                    onChange={(e) => setFormData({ ...formData, documento: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vencimento da 1ª Parcela</Label>
                  <Input
                    type="date"
                    value={formData.vencimento}
                    onChange={(e) => setFormData({ ...formData, vencimento: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Vencimento Final (Última Parcela)</Label>
                  <Input
                    type="date"
                    value={formData.vencimento_final}
                    onChange={(e) => setFormData({ ...formData, vencimento_final: e.target.value })}
                    placeholder="Opcional"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.vencimento_final ? 
                      `Gerará ${formData.parcelas_total} parcelas automáticas` :
                      'Deixe vazio para usar número de parcelas manual'
                    }
                  </p>
                </div>
              </div>

              {editingFinanceiro && (
                <div>
                  <Label>Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input-field"
                  >
                    <option value="em_aberto">Em Aberto</option>
                    <option value="pago">Pago</option>
                    <option value="vencido">Vencido</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
              )}

              {editingFinanceiro && formData.status === 'pago' && (
                <div>
                  <Label>Data de Pagamento</Label>
                  <Input
                    type="date"
                    value={formData.pagamento ? formData.pagamento.split('T')[0] : new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFormData({ ...formData, pagamento: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    required
                  />
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setModalEdicao(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-gradient-to-r from-red-600 to-orange-600">
                  {saving ? (editingFinanceiro ? 'Atualizando...' : 'Criando...') : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={loadData} className="w-full bg-gradient-to-r from-red-600 to-orange-600">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-red-600 to-orange-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 mb-2">Total {tipo === 'pagar' ? 'a Pagar' : 'a Receber'}</p>
              <p className="text-4xl font-bold">{formatCurrency(totalGeral)}</p>
              <p className="text-sm text-red-100 mt-1">{pessoasComMovimentacoes.length} pessoas</p>
            </div>
            <DollarSign className="w-16 h-16 text-red-200" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {pessoasComMovimentacoes.map((pessoa) => (
            <Card key={pessoa.id} className="card-hover cursor-pointer" onClick={() => handleVerDetalhes(pessoa)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center">
                      {pessoa.tipo === 'PF' ? (
                        <User className="w-6 h-6 text-red-600" />
                      ) : (
                        <Building className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">{pessoa.nome_razao}</h3>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-sm text-slate-600">{pessoa.cpf_cnpj}</span>
                        <div className="flex space-x-1">
                          {pessoa.cliente && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">Cliente</span>}
                          {pessoa.fornecedor && <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded">Fornecedor</span>}
                          {pessoa.motorista && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">Motorista</span>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-right mr-4">
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      {pessoa.totalAberto > 0 && (
                        <div>
                          <p className="text-xs text-slate-600">Em Aberto</p>
                          <p className="text-sm font-bold text-yellow-600">{formatCurrency(pessoa.totalAberto)}</p>
                        </div>
                      )}
                      {pessoa.totalVencido > 0 && (
                        <div>
                          <p className="text-xs text-slate-600">Vencido</p>
                          <p className="text-sm font-bold text-red-600">{formatCurrency(pessoa.totalVencido)}</p>
                        </div>
                      )}
                      {pessoa.totalPago > 0 && (
                        <div>
                          <p className="text-xs text-slate-600">Pago</p>
                          <p className="text-sm font-bold text-green-600">{formatCurrency(pessoa.totalPago)}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-end space-x-2">
                      <span className="text-2xl font-bold text-slate-900">{formatCurrency(pessoa.total)}</span>
                      <span className="text-sm text-slate-600">({pessoa.quantidade})</span>
                    </div>
                  </div>

                  <ChevronRight className="w-6 h-6 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {modalOpen && pessoaSelecionada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-xl font-bold">{pessoaSelecionada.nome_razao}</h2>
                <p className="text-sm text-red-100">{pessoaSelecionada.cpf_cnpj}</p>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Vencimento</th>
                      <th>Data Pagamento</th>
                      <th>Banco</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentacoesPessoa.map((mov) => (
                      <tr key={mov.id}>
                        <td className="font-semibold">{mov.titulo}</td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span>{formatDate(mov.vencimento)}</span>
                          </div>
                        </td>
                        <td>
                          {mov.status === 'pago' && mov.pagamento ? (
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-green-500" />
                              <span className="text-green-700 font-semibold">{formatDate(mov.pagamento)}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td>
                          <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                            {mov.banco || 'N/A'}
                          </span>
                        </td>
                        <td className="font-bold">{formatCurrency(mov.valor_liquido)}</td>
                        <td>
                          <span className={`status-badge status-${mov.status}`}>
                            {mov.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleEditar(mov, e)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {mov.status === 'pago' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-600 hover:bg-purple-50"
                                onClick={(e) => handleRecibo(mov.id, e)}
                                title="Baixar Recibo"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDelete({ isOpen: true, id: mov.id });
                              }}
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Total</p>
                    <p className="text-lg font-bold text-slate-900">
                      {formatCurrency(movimentacoesPessoa.reduce((sum, m) => sum + m.valor_liquido, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Pago</p>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(movimentacoesPessoa.filter(m => m.status === 'pago').reduce((sum, m) => sum + m.valor_liquido, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Em Aberto</p>
                    <p className="text-lg font-bold text-yellow-600">
                      {formatCurrency(movimentacoesPessoa.filter(m => m.status === 'em_aberto').reduce((sum, m) => sum + m.valor_liquido, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Vencido</p>
                    <p className="text-lg font-bold text-red-600">
                      {formatCurrency(movimentacoesPessoa.filter(m => m.status === 'vencido').reduce((sum, m) => sum + m.valor_liquido, 0))}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceirosAgrupados;
